import React from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  CheckCircle2,
  Star,
  Users,
  TrendingUp,
  Award,
  BookOpen,
  Brain,
  MapPin,
  Phone,
  Clock,
  Zap,
  Shield,
  Target,
  ChevronRight,
  GraduationCap,
  ArrowRight,
  BarChart2,
  Layers,
  FileText,
} from "lucide-react";
import FAQAccordion from "../components/FAQAccordion";

/* ─── constants ─────────────────────────────────────────────────────── */
const PHONE = "+919918519991";
const WA_MSG = encodeURIComponent(
  "Hello SSS Achievers, I found you while searching for Class 9 coaching in Ghazipur. Please share Class 9 course details. Thank you."
);
const WA_URL = `https://wa.me/919918519991?text=${WA_MSG}`;

const fade = {
  hidden: { opacity: 0, y: 20 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.1, duration: 0.5 },
  }),
};

/* ─── data ──────────────────────────────────────────────────────────── */
const STATS = [
  { value: "340+", label: "Students Enrolled", icon: Users },
  { value: "95%", label: "Board Success Rate", icon: TrendingUp },
  { value: "15+", label: "Expert Educators", icon: GraduationCap },
  { value: "5", label: "Core Subjects", icon: BookOpen },
];

const CLASS9_SUBJECTS = [
  {
    subject: "Mathematics",
    icon: "📐",
    topics: "Number Systems, Polynomials, Lines & Angles, Triangles, Coordinate Geometry, Heron's Formula, Statistics, Probability & more",
    color: "bg-blue-50 border-blue-200",
  },
  {
    subject: "Science",
    icon: "🔬",
    topics: "Matter in Our Surroundings, Atoms & Molecules, Motion, Gravitation, Work & Energy, Sound, Tissues, Diversity in Living Organisms, Natural Resources & more",
    color: "bg-emerald-50 border-emerald-200",
  },
  {
    subject: "Social Science",
    icon: "🗺️",
    topics: "History: French Revolution, Nazism; Geography: India Size & Location; Civics: Democracy; Economics: The Story of Village Palampur & more",
    color: "bg-amber-50 border-amber-200",
  },
  {
    subject: "English",
    icon: "📖",
    topics: "Beehive, Moments — comprehension, grammar, writing skills, letter & essay writing, literature analysis & more",
    color: "bg-violet-50 border-violet-200",
  },
  {
    subject: "Hindi",
    icon: "🖊️",
    topics: "Kshitij, Kritika — Gadya & Padya, grammar, vyakaran, writing and comprehension for UP Board & CBSE & more",
    color: "bg-rose-50 border-rose-200",
  },
];

const WHY_CLASS9 = [
  {
    icon: Layers,
    title: "Class 9 is the Foundation",
    desc: "Class 9 concepts directly feed into Class 10 board questions. Strong foundations here mean 90%+ in boards the next year.",
  },
  {
    icon: Brain,
    title: "Concept-Based Learning",
    desc: "Every Class 9 topic is explained from scratch — with examples, diagrams and real-world connections that make concepts stick.",
  },
  {
    icon: FileText,
    title: "School Exam Preparation",
    desc: "We align our teaching with school exam patterns — half-yearly and final exams — so students excel in school as well as coaching tests.",
  },
  {
    icon: BarChart2,
    title: "Weekly Tests & Analytics",
    desc: "Chapter-wise tests every week with detailed performance reports help identify and fix weak areas before they become problems in Class 10.",
  },
  {
    icon: Users,
    title: "Small Batches",
    desc: "Each Class 9 student gets individual attention in our small batches. No student struggles in silence at SSS Achievers.",
  },
  {
    icon: Zap,
    title: "Doubt Clearing Sessions",
    desc: "Dedicated doubt-clearing slots for each subject ensure every Class 9 student walks into exams fully confident.",
  },
  {
    icon: Shield,
    title: "Parent–Teacher Connect",
    desc: "Monthly progress reports and parent-teacher meetings keep parents informed about their child's academic progress in Class 9.",
  },
  {
    icon: Award,
    title: "95% Success Rate",
    desc: "Our Class 9 students consistently top their school exams and transition smoothly into scoring 90%+ in Class 10 CBSE boards.",
  },
];

const TESTIMONIALS = [
  {
    name: "Riya Gupta",
    cls: "Class 9, 2025",
    rating: 5,
    text: "SSS Achievers is the best Class 9 coaching in Ghazipur. My Maths and Science improved so much in just one semester. The weekly tests really help me track my progress!",
  },
  {
    name: "Aditya Rai",
    cls: "Class 9, 2025",
    rating: 5,
    text: "I joined SSS Achievers for Class 9 Science. The teachers explain every concept so clearly and the small batch means I can ask doubts without hesitation. Highly recommended!",
  },
  {
    name: "Geeta Singh (Parent)",
    cls: "Parent of Class 9 Student",
    rating: 5,
    text: "My child used to struggle in Class 9. Within 3 months at SSS Achievers, his scores improved from 60% to 84%. The faculty is dedicated and the monthly reports are very helpful.",
  },
  {
    name: "Sakshi Yadav",
    cls: "Class 9 → Class 10, 2025",
    rating: 5,
    text: "I joined SSS Achievers in Class 9 and continued to Class 10. The foundation built in Class 9 at SSS helped me score 91% in boards! Best coaching in Ghazipur.",
  },
];

const COMPARISON = [
  { feature: "Batch Size", sss: "Small Batch (focused)", others: "50+ students (crowded)" },
  { feature: "Subject Range", sss: "All 5 core subjects", others: "Usually Maths + Science only" },
  { feature: "School Exam Alignment", sss: "Aligned with school patterns", others: "Separate from school exams" },
  { feature: "Weekly Tests", sss: "Chapter-wise every week", others: "Monthly or less frequent" },
  { feature: "Doubt Sessions", sss: "Dedicated per subject", others: "During class only" },
  { feature: "Parent Updates", sss: "Monthly reports + meetings", others: "Rarely communicated" },
  { feature: "Demo Class", sss: "Free before enrollment", others: "Usually not offered" },
];

/* ─── component ─────────────────────────────────────────────────────── */
export default function Class9CoachingGhazipur() {
  return (
    <>
      <Helmet>
        <title>Class 9 Coaching in Ghazipur | Best Class 9 Tuition – SSS Achievers</title>
        <meta
          name="description"
          content="Best Class 9 coaching in Ghazipur at SSS Achievers. Expert coaching for all subjects — Maths, Science, SST, English. Small batches, weekly tests, 95% success rate. Free demo class!"
        />
        <meta
          name="keywords"
          content="class 9 coaching in Ghazipur, class 9 tuition Ghazipur, best class 9 coaching Ghazipur, class 9 maths science coaching Ghazipur, class IX coaching Ghazipur UP, SSS Achievers class 9, CBSE class 9 coaching Ghazipur, class 9 coaching near me Ghazipur"
        />
        <link rel="canonical" href="https://www.sssachievers.com/class-9-coaching-ghazipur" />
        <meta property="og:type" content="website" />
        <meta property="og:title" content="Class 9 Coaching in Ghazipur | Best Class 9 Tuition – SSS Achievers" />
        <meta property="og:description" content="Expert Class 9 coaching in Ghazipur for all subjects. Small batches, weekly tests, 95% success rate. Free demo class at SSS Achievers." />
        <meta property="og:url" content="https://www.sssachievers.com/class-9-coaching-ghazipur" />
        <meta property="og:image" content="https://www.sssachievers.com/logo512.png" />
        <meta property="og:locale" content="en_IN" />
        <meta property="og:site_name" content="SSS Achievers" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Class 9 Coaching in Ghazipur | SSS Achievers" />
        <meta name="twitter:description" content="Best Class 9 tuition in Ghazipur. Expert coaching for Maths, Science, SST. Free demo class." />
        <meta name="twitter:image" content="https://www.sssachievers.com/logo512.png" />
        <meta name="geo.region" content="IN-UP" />
        <meta name="geo.placename" content="Ghazipur, Uttar Pradesh" />
        <script type="application/ld+json">{`
          {
            "@context": "https://schema.org",
            "@graph": [
              {
                "@type": "EducationalOrganization",
                "@id": "https://www.sssachievers.com/#organization",
                "name": "SSS Achievers",
                "alternateName": ["Best Class 9 Coaching Ghazipur", "Class 9 Tuition Ghazipur"],
                "description": "SSS Achievers offers the best Class 9 coaching in Ghazipur for all subjects — Maths, Science, Social Science, English — with small batches and a 95% success rate.",
                "url": "https://www.sssachievers.com",
                "logo": "https://www.sssachievers.com/logo512.png",
                "telephone": "+919918519991",
                "address": {
                  "@type": "PostalAddress",
                  "streetAddress": "96/B, Badi Bagh",
                  "addressLocality": "Ghazipur",
                  "addressRegion": "Uttar Pradesh",
                  "postalCode": "233001",
                  "addressCountry": "IN"
                },
                "geo": {
                  "@type": "GeoCoordinates",
                  "latitude": 25.5825,
                  "longitude": 83.5805
                },
                "aggregateRating": {
                  "@type": "AggregateRating",
                  "ratingValue": "4.9",
                  "reviewCount": "340",
                  "bestRating": "5"
                }
              },
              {
                "@type": "FAQPage",
                "mainEntity": [
                  {
                    "@type": "Question",
                    "name": "Which is the best Class 9 coaching in Ghazipur?",
                    "acceptedAnswer": {
                      "@type": "Answer",
                      "text": "SSS Achievers is the best Class 9 coaching institute in Ghazipur. With expert faculty for all subjects, small, focused batches, weekly tests and a 95% success rate, SSS Achievers is the #1 Class 9 coaching centre in Ghazipur."
                    }
                  },
                  {
                    "@type": "Question",
                    "name": "Why is Class 9 coaching important?",
                    "acceptedAnswer": {
                      "@type": "Answer",
                      "text": "Class 9 builds the conceptual foundation for Class 10 board exams. Students who have strong Class 9 fundamentals — especially in Maths and Science — find it significantly easier to score 90%+ in CBSE Class 10 boards. SSS Achievers makes that foundation rock-solid."
                    }
                  },
                  {
                    "@type": "Question",
                    "name": "What subjects does SSS Achievers cover for Class 9?",
                    "acceptedAnswer": {
                      "@type": "Answer",
                      "text": "SSS Achievers covers all core Class 9 subjects — Mathematics, Science (Physics, Chemistry, Biology), Social Science, English and Hindi — with dedicated subject experts for each."
                    }
                  },
                  {
                    "@type": "Question",
                    "name": "Is there a free demo class for Class 9 at SSS Achievers?",
                    "acceptedAnswer": {
                      "@type": "Answer",
                      "text": "Yes — SSS Achievers offers a completely free demo class for Class 9. Attend a session, experience the teaching quality and meet the faculty before enrolling."
                    }
                  },
                  {
                    "@type": "Question",
                    "name": "Does SSS Achievers prepare Class 9 students for school exams too?",
                    "acceptedAnswer": {
                      "@type": "Answer",
                      "text": "Yes. SSS Achievers aligns its Class 9 teaching with school exam patterns — half-yearly and final exams — so students perform well in both their school exams and coaching tests simultaneously."
                    }
                  }
                ]
              },
              {
                "@type": "BreadcrumbList",
                "itemListElement": [
                  { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://www.sssachievers.com/" },
                  { "@type": "ListItem", "position": 2, "name": "Class 9 Coaching in Ghazipur", "item": "https://www.sssachievers.com/class-9-coaching-ghazipur" }
                ]
              }
            ]
          }
        `}</script>
      </Helmet>

      <main className="bg-white text-slate-900">
        {/* ══ HERO ══ */}
        <section className="relative overflow-hidden bg-gradient-to-br from-[#0097b2]/8 via-white to-[#fcbf0a]/8 pt-10 pb-14 md:pt-20 md:pb-24">
          <div className="absolute inset-0 pointer-events-none overflow-hidden">
            <div className="absolute -top-32 -right-32 w-72 h-72 bg-[#0097b2]/10 rounded-full blur-3xl" />
            <div className="absolute -bottom-32 -left-32 w-72 h-72 bg-[#fcbf0a]/10 rounded-full blur-3xl" />
          </div>

          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <nav aria-label="breadcrumb" className="mb-6 flex items-center gap-2 text-sm text-slate-500">
              <Link to="/" className="hover:text-[#0097b2] transition">Home</Link>
              <ChevronRight className="w-4 h-4" />
              <Link to="/courses" className="hover:text-[#0097b2] transition">Courses</Link>
              <ChevronRight className="w-4 h-4" />
              <span className="text-[#0097b2] font-medium">Class 9 Coaching in Ghazipur</span>
            </nav>

            <div className="grid md:grid-cols-2 gap-10 items-center">
              <motion.div initial="hidden" animate="visible" variants={fade} className="space-y-6">
                <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#fcbf0a]/20 border border-[#fcbf0a]/40">
                  <Star className="w-4 h-4 text-amber-500 fill-amber-500" />
                  <span className="text-sm font-semibold text-amber-700">Build Your Foundation for Class 10 Boards</span>
                </div>

                <h1 className="text-4xl md:text-5xl font-bold leading-tight text-slate-900">
                  Best <span className="text-[#0097b2]">Class 9 Coaching</span> in Ghazipur — All Subjects
                </h1>

                <p className="text-base md:text-lg text-slate-600 max-w-xl leading-relaxed">
                  SSS Achievers provides the strongest Class 9 coaching in Ghazipur — covering Maths, Science, Social Science, English and Hindi. We build deep conceptual foundations in Class 9 that directly translate to 90%+ in Class 10 CBSE boards.
                </p>

                <div className="flex flex-col sm:flex-row gap-3">
                  <a href={WA_URL} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-2 px-6 py-3.5 bg-[#0097b2] text-white font-semibold rounded hover:bg-[#007a91] shadow-md hover:shadow-lg transition">
                    Book Free Demo Class 9 <ArrowRight className="w-4 h-4" />
                  </a>
                  <a href={`tel:${PHONE}`} className="inline-flex items-center justify-center gap-2 px-6 py-3.5 border-2 border-[#0097b2] text-[#0097b2] font-semibold rounded hover:bg-[#0097b2]/5 transition">
                    <Phone className="w-4 h-4" /> Call: +91 99185 19991
                  </a>
                </div>

                <div className="flex flex-wrap gap-4 pt-2">
                  {["All Core Subjects", "Small Batches", "Weekly Tests", "School Exam Aligned"].map((t) => (
                    <span key={t} className="inline-flex items-center gap-1.5 text-sm text-slate-600">
                      <CheckCircle2 className="w-4 h-4 text-[#0097b2]" /> {t}
                    </span>
                  ))}
                </div>
              </motion.div>

              <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.6, delay: 0.2 }} className="grid grid-cols-2 gap-4">
                {STATS.map(({ value, label, icon: Icon }) => (
                  <div key={label} className="bg-white rounded-2xl p-6 shadow-md border border-gray-100 flex flex-col items-center text-center hover:shadow-lg transition">
                    <div className="w-12 h-12 rounded-xl bg-[#0097b2]/10 flex items-center justify-center mb-3">
                      <Icon className="w-6 h-6 text-[#0097b2]" />
                    </div>
                    <span className="text-3xl font-bold text-[#0097b2]">{value}</span>
                    <span className="text-sm text-slate-500 mt-1 font-medium">{label}</span>
                  </div>
                ))}
                <div className="col-span-2 bg-gradient-to-r from-[#0097b2] to-[#006f85] rounded-2xl p-5 text-white flex items-center gap-4">
                  <div>
                    <div className="flex gap-1 mb-1">
                      {[1,2,3,4,5].map((s) => <Star key={s} className="w-5 h-5 fill-[#fcbf0a] text-[#fcbf0a]" />)}
                    </div>
                    <p className="font-bold text-lg">4.9 / 5.0 Rating</p>
                    <p className="text-blue-100 text-sm">Based on 340+ student reviews</p>
                  </div>
                  <div className="ml-auto text-right">
                    <p className="text-blue-100 text-xs uppercase tracking-wider font-semibold">Class</p>
                    <p className="text-3xl font-bold">9th</p>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* ══ WHY CLASS 9 MATTERS ══ */}
        <section className="py-14 bg-gradient-to-r from-[#0097b2] to-[#006f85]">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center text-white">
              <h2 className="text-2xl md:text-3xl font-bold mb-4">Why Class 9 Coaching is Critical for Your Child's Future</h2>
              <p className="text-blue-100 text-base max-w-3xl mx-auto leading-relaxed">
                Class 9 is not just another year — it is the <strong className="text-white">make-or-break foundation</strong> for Class 10 board exams. Students who master Class 9 concepts in Maths, Science and Social Science find Class 10 boards significantly easier and consistently score 90%+. A weak Class 9 means struggling in Class 10. <strong className="text-white">Don't let that happen.</strong>
              </p>
            </motion.div>
            <div className="grid sm:grid-cols-3 gap-6 mt-8">
              {[
                { title: "Class 9 Maths", desc: "Polynomials, Triangles & Coordinate Geometry directly appear in Class 10 boards" },
                { title: "Class 9 Science", desc: "Motion, Atoms & Molecules, and Life Processes are core Class 10 board topics" },
                { title: "Class 9 SST", desc: "History & Civics foundations build directly into Class 10 board chapters" },
              ].map(({ title, desc }) => (
                <div key={title} className="bg-white/10 rounded-2xl p-5 border border-white/20">
                  <h3 className="font-bold text-white mb-2">{title}</h3>
                  <p className="text-blue-100 text-sm">{desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ══ SUBJECTS ══ */}
        <section className="py-16 md:py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <motion.h2 initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-3xl md:text-4xl font-bold">
                Class 9 Syllabus We Master at <span className="text-[#0097b2]">SSS Achievers Ghazipur</span>
              </motion.h2>
              <p className="mt-3 text-slate-600">Specialist faculty for every Class 9 CBSE subject — nothing left to chance</p>
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {CLASS9_SUBJECTS.map(({ subject, icon, topics, color }, i) => (
                <motion.div key={subject} initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.15 }} custom={i} variants={fade}
                  className={`rounded-2xl border-2 ${color} bg-white p-6 hover:shadow-lg transition-all`}>
                  <div className="text-3xl mb-3">{icon}</div>
                  <h3 className="font-bold text-slate-800 text-lg mb-2">{subject}</h3>
                  <p className="text-slate-500 text-sm leading-relaxed">{topics}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* ══ WHY SSS ══ */}
        <section className="py-16 md:py-20 bg-gradient-to-br from-[#0097b2]/5 to-[#fcbf0a]/5">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <motion.h2 initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-3xl md:text-4xl font-bold">
                Why SSS Achievers is the <span className="text-[#0097b2]">Best Class 9 Coaching</span> in Ghazipur
              </motion.h2>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {WHY_CLASS9.map(({ icon: Icon, title, desc }, i) => (
                <motion.div key={title} initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.15 }} custom={i} variants={fade}
                  className="group p-6 rounded-2xl border-2 border-gray-100 hover:border-[#0097b2]/40 hover:shadow-lg transition-all duration-300 bg-white">
                  <div className="w-12 h-12 rounded-xl bg-[#0097b2]/10 flex items-center justify-center mb-4 group-hover:bg-[#0097b2]/20 transition-colors">
                    <Icon className="w-6 h-6 text-[#0097b2]" />
                  </div>
                  <h3 className="font-bold text-slate-800 mb-2 text-base">{title}</h3>
                  <p className="text-slate-500 text-sm leading-relaxed">{desc}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* ══ COMPARISON ══ */}
        <section className="py-16 md:py-20 bg-white">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <motion.h2 initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-3xl md:text-4xl font-bold">
                SSS Achievers Class 9 vs. Other Coaching Centres in Ghazipur
              </motion.h2>
            </div>
            <div className="overflow-hidden rounded-2xl border-2 border-gray-100 shadow-sm">
              <div className="grid grid-cols-3 bg-[#0097b2] text-white text-sm font-bold">
                <div className="px-5 py-4">Feature</div>
                <div className="px-5 py-4 text-center border-l border-white/20">SSS Achievers ✓</div>
                <div className="px-5 py-4 text-center border-l border-white/20 text-blue-100">Others</div>
              </div>
              {COMPARISON.map(({ feature, sss, others }, i) => (
                <div key={feature} className={`grid grid-cols-3 text-sm border-b border-gray-100 ${i % 2 === 0 ? "bg-white" : "bg-gray-50"}`}>
                  <div className="px-5 py-4 font-semibold text-slate-700">{feature}</div>
                  <div className="px-5 py-4 text-center border-l border-gray-100">
                    <div className="flex items-center justify-center gap-2">
                      <CheckCircle2 className="w-4 h-4 text-[#0097b2] flex-shrink-0" />
                      <span className="text-slate-700 font-medium">{sss}</span>
                    </div>
                  </div>
                  <div className="px-5 py-4 text-center border-l border-gray-100 text-slate-400 text-xs">{others}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ══ TESTIMONIALS ══ */}
        <section className="py-16 md:py-20 bg-gradient-to-br from-[#0097b2]/5 to-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <motion.h2 initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-3xl md:text-4xl font-bold">
                Class 9 Students Who Excelled at <span className="text-[#0097b2]">SSS Achievers Ghazipur</span>
              </motion.h2>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {TESTIMONIALS.map(({ name, cls, rating, text }, i) => (
                <motion.div key={name} initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.15 }} custom={i} variants={fade}
                  className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100 hover:shadow-md transition flex flex-col">
                  <div className="flex gap-0.5 mb-3">
                    {Array.from({ length: rating }).map((_, s) => <Star key={s} className="w-4 h-4 fill-[#fcbf0a] text-[#fcbf0a]" />)}
                  </div>
                  <p className="text-slate-600 text-sm leading-relaxed flex-1 italic">"{text}"</p>
                  <div className="mt-4 pt-4 border-t border-gray-100">
                    <p className="font-semibold text-slate-800 text-sm">{name}</p>
                    <p className="text-xs text-slate-400 mt-0.5">{cls}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* ══ CAMPUS GLIMPSE ══ */}
        <section className="py-14 md:py-16 bg-slate-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-8">
              <motion.h2
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="text-2xl md:text-3xl font-bold"
              >
                Where Class 9 Students <span className="text-[#0097b2]">Learn & Grow — SSS Achievers Ghazipur</span>
              </motion.h2>
              <p className="mt-2 text-slate-500 text-sm">Our classrooms, campus and the moments that matter</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {[
                {
                  src: "/images/SSS%20Achievers%20classroom.avif",
                  alt: "SSS Achievers Class 9 coaching classroom in Ghazipur — modern AI-powered smart classroom where Class 9 students build their board exam foundation",
                  caption: "🏫 Class 9 Classroom — SSS Achievers Ghazipur",
                },
                {
                  src: "/images/sss%20achievers%20building%20photo.avif",
                  alt: "SSS Achievers Class 9 coaching institute building in Ghazipur — located at Badi Bagh, opposite Raj Nursing Home, Ghazipur UP 233001",
                  caption: "🏢 SSS Achievers Campus — Ghazipur",
                },
                {
                  src: "/images/gallery/student-group.jpg",
                  alt: "SSS Achievers Class 9 student batch group photo in Ghazipur — Class 9 students at the best coaching institute in Ghazipur, Uttar Pradesh",
                  caption: "👨‍🎓 Class 9 Batch — SSS Achievers Ghazipur",
                },
              ].map(({ src, alt, caption }, i) => (
                <motion.figure
                  key={caption}
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.1 }}
                  className="rounded-2xl overflow-hidden shadow-md group"
                >
                  <img
                    src={src}
                    alt={alt}
                    className="w-full h-56 object-cover group-hover:scale-105 transition-transform duration-500"
                    loading="lazy"
                    width="600"
                    height="224"
                  />
                  <figcaption className="bg-white px-4 py-3 text-sm font-semibold text-slate-700 border-t border-gray-100">
                    {caption}
                  </figcaption>
                </motion.figure>
              ))}
            </div>
          </div>
        </section>

        {/* ══ LOCATION ══ */}
        <section className="py-16 md:py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <motion.div initial={{ opacity: 0, x: -20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} className="space-y-5">
                <h2 className="text-3xl md:text-4xl font-bold">
                  Enroll for <span className="text-[#0097b2]">Class 9 Coaching in Ghazipur</span> Today
                </h2>
                <p className="text-slate-600">Visit SSS Achievers at our Ghazipur centre or enquire via WhatsApp/call. Limited seats available per batch.</p>
                {[
                  { icon: MapPin, text: "96/B, Badi Bagh, Opp. Raj Nursing Home, Ghazipur, UP – 233001" },
                  { icon: Phone, text: "+91 99185 19991", href: `tel:${PHONE}` },
                  { icon: Clock, text: "Mon–Sat: 9:00 AM – 7:00 PM" },
                ].map(({ icon: Icon, text, href }) => (
                  <div key={text} className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-lg bg-[#0097b2]/10 flex items-center justify-center flex-shrink-0">
                      <Icon className="w-5 h-5 text-[#0097b2]" />
                    </div>
                    {href ? <a href={href} className="text-sm text-[#0097b2] font-semibold hover:underline mt-2">{text}</a> : <p className="text-sm text-slate-600 mt-2">{text}</p>}
                  </div>
                ))}
                <div className="flex flex-wrap gap-3 pt-2">
                  <Link to="/courses/class-9-foundation" onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })} className="inline-flex items-center gap-2 px-5 py-3 bg-[#0097b2] text-white font-semibold rounded hover:bg-[#007a91] transition shadow-md text-sm">
                    View Class 9 Course Details <ArrowRight className="w-4 h-4" />
                  </Link>
                  <Link to="/courses/class-10-board-preparation" onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })} className="inline-flex items-center gap-2 px-5 py-3 border-2 border-[#0097b2] text-[#0097b2] font-semibold rounded hover:bg-[#0097b2]/5 transition text-sm">
                    Class 10 Board Prep
                  </Link>
                </div>
              </motion.div>

              <motion.div initial={{ opacity: 0, x: 20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} className="rounded-3xl overflow-hidden border-2 border-gray-100 shadow-lg">
                <div className="bg-[#0097b2] px-6 py-4 flex items-center gap-3">
                  <MapPin className="w-5 h-5 text-white" />
                  <span className="text-white font-semibold">SSS Achievers — Ghazipur</span>
                </div>
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3598.750962143519!2d83.56063587575478!3d25.57995091609078!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x2bb0bb23b08ac59%3A0x4c04724e0b4e6039!2sSSS%20ACHIEVERS%20Coaching%20Centre%20Ghazipur!5e0!3m2!1sen!2sin!4v1775367597808!5m2!1sen!2sin"
                  width="100%"
                  height="280"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="SSS Achievers Class 9 Coaching Ghazipur"
                />
              </motion.div>
            </div>
          </div>
        </section>

        {/* ══ FAQ ══ */}
        <section className="py-16 md:py-20 bg-gradient-to-br from-[#0097b2]/5 to-white">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-10">
              <motion.h2 initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-3xl md:text-4xl font-bold">Frequently Asked Questions</motion.h2>
              <p className="mt-3 text-slate-500 text-sm">About Class 9 coaching in Ghazipur at SSS Achievers</p>
            </div>
            <FAQAccordion
              faqs={[
                { q: "Which is the best Class 9 coaching in Ghazipur?", a: "SSS Achievers is the best Class 9 coaching institute in Ghazipur. With expert faculty for all subjects, small, focused batches, weekly chapter tests and a 95% board success rate, SSS Achievers gives Class 9 students the strongest academic foundation in Ghazipur." },
                { q: "Why is Class 9 coaching important for Class 10 boards?", a: "Class 9 forms the direct foundation for Class 10 board exams. Topics like Polynomials, Triangles, Motion, Atoms & Molecules studied in Class 9 directly appear in Class 10 CBSE boards. Students with strong Class 9 fundamentals consistently score 90%+ in boards." },
                { q: "What subjects does SSS Achievers teach for Class 9?", a: "SSS Achievers provides coaching for all Class 9 core subjects — Mathematics, Science (Physics, Chemistry, Biology), Social Science, English and Hindi — each taught by dedicated subject specialists." },
                { q: "Does SSS Achievers cover Class 9 school exams too?", a: "Yes. SSS Achievers aligns its Class 9 coaching with school exam patterns — half-yearly and final exams. Students benefit from both coaching excellence and improved school exam performance simultaneously." },
                { q: "Is there a free demo class for Class 9 at SSS Achievers?", a: "Yes — SSS Achievers offers a completely free demo class for Class 9. Students can attend, experience the teaching quality, meet the faculty and assess the classroom environment before enrolling." },
                { q: "What is the batch size for Class 9 at SSS Achievers?", a: "Class 9 batches at SSS Achievers are kept intentionally small to ensure every student gets individual attention, regular doubt-clearing and personalised feedback from faculty." },
              ]}
            />
          </div>
        </section>

        {/* ══ FINAL CTA ══ */}
        <section className="py-16 md:py-20 bg-gradient-to-r from-[#0097b2] to-[#006f85] relative overflow-hidden">
          <div className="absolute inset-0 pointer-events-none">
            <div className="absolute -top-16 -right-16 w-64 h-64 bg-white/5 rounded-full blur-3xl" />
            <div className="absolute -bottom-16 -left-16 w-64 h-64 bg-[#fcbf0a]/10 rounded-full blur-3xl" />
          </div>
          <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="space-y-6">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 border border-white/20">
                <Star className="w-4 h-4 text-[#fcbf0a] fill-[#fcbf0a]" />
                <span className="text-white text-sm font-semibold">Ghazipur's #1 Class 9 Coaching Centre</span>
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-white">Give Your Child the Best Class 9 Foundation in Ghazipur</h2>
              <p className="text-blue-100 text-base md:text-lg max-w-2xl mx-auto">
                Limited seats available. Book a free demo Class 9 class today and experience why 340+ Ghazipur students trust SSS Achievers.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a href={WA_URL} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-[#fcbf0a] text-gray-900 font-bold rounded hover:bg-[#e6ac00] transition shadow-lg text-base">
                  Book Free Demo Class 9 <ArrowRight className="w-5 h-5" />
                </a>
                <a href={`tel:${PHONE}`} className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-white/10 border-2 border-white/30 text-white font-bold rounded hover:bg-white/20 transition text-base">
                  <Phone className="w-5 h-5" /> +91 99185 19991
                </a>
              </div>
              <p className="text-blue-200 text-xs mt-4">📍 96/B, Badi Bagh, Opp. Raj Nursing Home, Ghazipur, UP – 233001</p>
            </motion.div>
          </div>
        </section>
      </main>
    </>
  );
}
